import { useState } from 'react';
import type { CreditSettings, UpdateCreditSettingsRequest } from '@nadoutong/contracts';
import {
  CREDIT_STATEMENT_ANCHOR_DAY_MAX,
  CREDIT_STATEMENT_ANCHOR_DAY_MIN,
} from '@nadoutong/contracts';
import { useCreditSettings, useUpdateCreditSettings } from '../api/credit.ts';
import {
  useCreditCustomerMailSettings,
  useUpdateCreditCustomerMailSettings,
} from '../api/creditCustomerMail.ts';
import { coerceErrorMessage, useToast } from '../components/Toast.tsx';
import { formatUsdMicros, parseAmountInputToMinorUnits } from '../components/formatters.ts';
import { useSession } from '../hooks/useSession.tsx';
import { ROLE_OPS_ADMIN, hasAnyOpsRole } from '../auth/opsRoles.ts';
import { useT } from '../i18n/index.ts';
import {
  Button,
  Chip,
  ConfirmDialog,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  Panel,
  Skeleton,
  Switch,
} from '../ui/index.ts';

/**
 * Admin credit-line defaults panel (epic #1033 E4-C1, #1125; router#1999,
 * router#2061). The platform singleton `credit_settings` — fallback
 * limit, payment terms after statement, and the monthly statement
 * anchor day. `default_cycle_days` is leftover under monthly-anchor
 * billing and is not shown or written here; the column stays in the
 * database. Money is integer micro-USD; the limit input is USD (two
 * decimals → cents → micros). Edits accumulate in a local draft and save
 * as one partial PUT; the platform audits the change.
 */

const MICROS_PER_CENT = 10_000;

interface DraftState {
  readonly limit: string;
  readonly grace: string;
  readonly anchor: string;
}

function draftFromSettings(settings: CreditSettings): DraftState {
  return {
    limit: (settings.default_limit_usd_micros / 1_000_000).toFixed(2),
    grace: String(settings.default_grace_days),
    anchor: String(settings.statement_anchor_day_utc8),
  };
}

export function CreditSettingsPage(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const { user } = useSession();
  const isAdmin = hasAnyOpsRole(user?.roles ?? [], [ROLE_OPS_ADMIN]);

  const query = useCreditSettings(isAdmin);
  const update = useUpdateCreditSettings();
  const mailQuery = useCreditCustomerMailSettings(isAdmin);
  const updateMail = useUpdateCreditCustomerMailSettings();
  const [draft, setDraft] = useState<DraftState | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [pendingMailEnabled, setPendingMailEnabled] = useState<boolean | null>(null);
  const [mailReason, setMailReason] = useState('');

  const settings = query.data?.settings ?? null;
  const effectiveDraft = draft ?? (settings === null ? null : draftFromSettings(settings));

  function patchDraft(patch: Partial<DraftState>): void {
    const base = effectiveDraft ?? { limit: '', grace: '', anchor: '' };
    setDraft({ ...base, ...patch });
    setValidationError(null);
  }

  function handleSave(): void {
    if (settings === null || effectiveDraft === null) {
      return;
    }
    const parsedLimit = parseAmountInputToMinorUnits(effectiveDraft.limit);
    if (!parsedLimit.ok || parsedLimit.minorUnits < 0) {
      setValidationError(t('credit.settings.error.limit'));
      return;
    }
    const limitMicros = parsedLimit.minorUnits * MICROS_PER_CENT;
    if (!/^\d+$/.test(effectiveDraft.grace.trim())) {
      setValidationError(t('credit.settings.error.grace'));
      return;
    }
    const grace = Number.parseInt(effectiveDraft.grace.trim(), 10);
    if (!/^\d+$/.test(effectiveDraft.anchor.trim())) {
      setValidationError(t('credit.settings.error.anchor'));
      return;
    }
    const anchor = Number.parseInt(effectiveDraft.anchor.trim(), 10);
    if (anchor < CREDIT_STATEMENT_ANCHOR_DAY_MIN || anchor > CREDIT_STATEMENT_ANCHOR_DAY_MAX) {
      setValidationError(t('credit.settings.error.anchor'));
      return;
    }
    setValidationError(null);

    const body: UpdateCreditSettingsRequest = {
      default_limit_usd_micros: limitMicros,
      default_grace_days: grace,
      statement_anchor_day_utc8: anchor,
    };
    update.mutate(body, {
      onSuccess: () => {
        setDraft(null);
        toast.success(t('credit.settings.toast.saved'));
      },
      onError: (error) => {
        toast.error(coerceErrorMessage(error));
      },
    });
  }

  if (!isAdmin) {
    return (
      <div className="flex flex-col gap-6" data-testid="credit-settings-page">
        <PageHeader title={t('credit.settings.title')} sub={t('credit.settings.subtitle')} />
        <EmptyState
          title={t('credit.settings.forbidden')}
          testId="credit-settings-forbidden"
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6" data-testid="credit-settings-page">
      <PageHeader title={t('credit.settings.title')} sub={t('credit.settings.subtitle')} />
      <Panel caption={t('credit.settings.panel')} testId="credit-settings-panel">
        {query.isPending ? (
          <div className="p-4">
            <Skeleton className="h-40 w-full" />
          </div>
        ) : null}
        {query.isError ? (
          <EmptyState
            title={t('credit.settings.load_error')}
            hint={coerceErrorMessage(query.error)}
            action={<Button onClick={() => void query.refetch()}>{t('common.retry')}</Button>}
            testId="credit-settings-error"
          />
        ) : null}
        {settings !== null && effectiveDraft !== null ? (
          <div className="flex flex-col gap-4 p-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                label={t('credit.settings.limit_label')}
                fieldId="credit-settings-limit"
                hint={t('credit.settings.limit_hint', {
                  current: formatUsdMicros(settings.default_limit_usd_micros, false),
                })}
              >
                <Input
                  id="credit-settings-limit"
                  value={effectiveDraft.limit}
                  onChange={(event) => patchDraft({ limit: event.currentTarget.value })}
                  inputMode="decimal"
                  data-testid="credit-settings-limit"
                />
              </FormField>
              <FormField
                label={t('credit.settings.grace_label')}
                fieldId="credit-settings-grace"
                hint={t('credit.settings.grace_hint')}
              >
                <Input
                  id="credit-settings-grace"
                  value={effectiveDraft.grace}
                  onChange={(event) => patchDraft({ grace: event.currentTarget.value })}
                  inputMode="numeric"
                  data-testid="credit-settings-grace"
                />
              </FormField>
              <FormField
                label={t('credit.settings.anchor_label')}
                fieldId="credit-settings-anchor"
                hint={t('credit.settings.anchor_hint')}
              >
                <Input
                  id="credit-settings-anchor"
                  value={effectiveDraft.anchor}
                  onChange={(event) => patchDraft({ anchor: event.currentTarget.value })}
                  inputMode="numeric"
                  data-testid="credit-settings-anchor"
                />
              </FormField>
            </div>

            {validationError !== null ? (
              <p
                role="alert"
                data-testid="credit-settings-validation-error"
                className="m-0 rounded-lg border border-bad-line bg-bad-dim/50 px-3 py-2 text-dense text-bad-bright"
              >
                {validationError}
              </p>
            ) : null}

            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setDraft(null);
                  setValidationError(null);
                }}
                disabled={draft === null || update.isPending}
                data-testid="credit-settings-reset"
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                loading={update.isPending}
                disabled={draft === null}
                data-testid="credit-settings-save"
              >
                {t('credit.settings.save')}
              </Button>
            </div>
          </div>
        ) : null}
      </Panel>

      <Panel
        caption={t('credit.mail.switch.caption')}
        padded
        testId="credit-mail-switch-panel"
        actions={
          mailQuery.data !== undefined ? (
            <Chip
              tone={mailQuery.data.settings.enabled ? 'ok' : 'bad'}
              testId="credit-mail-switch-chip"
            >
              {mailQuery.data.settings.enabled
                ? t('credit.mail.switch.on')
                : t('credit.mail.switch.off')}
            </Chip>
          ) : undefined
        }
      >
        {mailQuery.isPending ? <Skeleton className="h-16 w-full" /> : null}
        {mailQuery.isError ? (
          <EmptyState
            title={t('credit.mail.switch.load_error')}
            hint={coerceErrorMessage(mailQuery.error)}
            action={<Button onClick={() => void mailQuery.refetch()}>{t('common.retry')}</Button>}
            testId="credit-mail-switch-error"
          />
        ) : null}
        {mailQuery.data !== undefined ? (
          <div className="flex flex-col gap-3">
            <p className="m-0 text-dense text-paper-mid">{t('credit.mail.switch.hint')}</p>
            <Switch
              checked={mailQuery.data.settings.enabled}
              disabled={updateMail.isPending}
              onCheckedChange={(next) => setPendingMailEnabled(next)}
              label={t('credit.mail.switch.toggle')}
              testId="credit-mail-switch"
            />
          </div>
        ) : null}
      </Panel>

      <ConfirmDialog
        open={pendingMailEnabled !== null}
        onOpenChange={(open) => {
          if (!open && !updateMail.isPending) {
            setPendingMailEnabled(null);
            setMailReason('');
          }
        }}
        title={
          pendingMailEnabled === true
            ? t('credit.mail.switch.enable_title')
            : t('credit.mail.switch.disable_title')
        }
        body={
          <div className="flex flex-col gap-3">
            <p className="m-0">
              {pendingMailEnabled === true
                ? t('credit.mail.switch.enable_body')
                : t('credit.mail.switch.disable_body')}
            </p>
            <FormField
              label={t('credit.mail.switch.reason_label')}
              fieldId="credit-mail-switch-reason"
              hint={t('credit.mail.switch.reason_hint')}
            >
              <Input
                id="credit-mail-switch-reason"
                value={mailReason}
                onChange={(event) => setMailReason(event.currentTarget.value)}
                data-testid="credit-mail-switch-reason"
              />
            </FormField>
          </div>
        }
        confirmLabel={t('credit.mail.switch.confirm')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        tone={pendingMailEnabled === true ? 'default' : 'danger'}
        loading={updateMail.isPending}
        testId="credit-mail-switch-dialog"
        onConfirm={() => {
          if (pendingMailEnabled === null) return;
          updateMail.mutate(
            {
              enabled: pendingMailEnabled,
              reason: mailReason.trim().length > 0 ? mailReason.trim() : null,
            },
            {
              onSuccess: () => {
                toast.success(
                  pendingMailEnabled
                    ? t('credit.mail.switch.enabled_toast')
                    : t('credit.mail.switch.disabled_toast'),
                );
                setPendingMailEnabled(null);
                setMailReason('');
              },
              onError: (error) => {
                toast.error(coerceErrorMessage(error));
              },
            },
          );
        }}
      />
    </div>
  );
}
